import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { epsteinSource } from "../lib/api";
import { KIND_META, REL_META } from "../components/epstein/efConsts";
import { EvidenceFragments } from "../components/epstein/EvidenceFragments";
import { RecordMediaGallery } from "../components/media/PublishedMedia";

const EpsteinSource = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const r = await epsteinSource(id);
        if (active) setData(r);
      } catch {
        toast.error("Source not found.");
        if (active) setData(null);
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [id]);

  if (loading) {
    return <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center font-mono text-[#757065]">// LOADING SOURCE…</div>;
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex flex-col items-center justify-center gap-3" data-testid="ef-source-404">
        <div className="font-mono text-xs text-[#757065]">// SOURCE NOT FOUND</div>
        <Link to="/epstein-files" className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">← back to the file</Link>
      </div>
    );
  }

  const { source, entities, relationships } = data;

  return (
    <div className="min-h-screen bg-[#0F0F0F]" data-testid={`ef-source-page-${source.id}`}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-10 py-6 md:py-10">
        <Link to="/epstein-files" data-testid="ef-source-back"
          className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39E93] hover:text-[#D92D20] mb-6">
          <ArrowLeft size={12} /> Back to evidence board
        </Link>

        <div className="flex flex-wrap items-center gap-2 mb-2">
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#5DD4B3] border border-[#5DD4B3]/50 px-2 py-0.5">
            {(source.source_type || "source").replace(/_/g, " ")}
          </span>
          {source.publisher && <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#A39E93]">{source.publisher}</span>}
          {source.demo && <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#E0A526] border border-[#E0A526]/50 px-2 py-0.5">// DEMO</span>}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl text-[#E4DFD3] mb-3">{source.title}</h1>

        <a href={source.url} target="_blank" rel="noopener noreferrer"
           className="inline-flex items-center gap-1.5 font-mono text-xs text-[#A39E93] hover:text-[#D92D20] break-all">
          {source.url} <ExternalLink size={11} />
        </a>

        {source.excerpt && (
          <blockquote className="mt-5 border-l-2 border-[#D92D20] pl-3 italic text-[#CBB892]">
            “{source.excerpt}”
          </blockquote>
        )}

        <section className="mt-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-3">// LINKED ENTITIES</div>
          {entities.length === 0 ? (
            <div className="font-mono text-xs text-[#757065]">None.</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2">
              {entities.map((e) => {
                const m = KIND_META[e.kind] || KIND_META.reference;
                return (
                  <Link key={e.id} to={`/epstein-files/entity/${e.slug}`}
                    className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2 hover:border-[#D92D20]/60 block"
                    data-testid={`ef-source-entity-${e.id}`}>
                    <span style={{ color: m.color }} className="font-mono text-[8px] uppercase tracking-[0.25em]">
                      {m.label}
                    </span>
                    <div className="font-heading text-sm text-[#E4DFD3]">{e.name}</div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        <section className="mt-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] mb-3">// LINKED RELATIONSHIPS</div>
          {relationships.length === 0 ? (
            <div className="font-mono text-xs text-[#757065]">None.</div>
          ) : (
            <div className="space-y-2">
              {relationships.map((r) => {
                const rm = REL_META[r.type] || REL_META.other_editor_defined;
                return (
                  <div key={r.id} className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2" data-testid={`ef-source-rel-${r.id}`}>
                    <span style={{ color: rm.color, borderColor: rm.color }}
                          className="font-mono text-[8px] uppercase tracking-[0.25em] border px-1.5 py-0.5">
                      {rm.label}
                    </span>
                    {r.summary && <div className="text-xs text-[#A39E93] mt-1">{r.summary}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Evidence fragments under this source */}
        <EvidenceFragments
          sourceId={source.id}
          title="// FRAGMENTS FROM THIS SOURCE"
          emptyLabel="No fragments anchored under this source yet."
          testidPrefix="ef-source-fragments"
        />

        {/* Authenticated Media Library photographs (published only) */}
        <RecordMediaGallery targetType="document" targetId={source.id} />
      </div>
    </div>
  );
};

export default EpsteinSource;
